"use client";

import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { SearchProduct } from "@/packages/types/products";
import { fetchSearchSuggestion } from "@/api-service/product";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import SearchDropdownV2 from "./SearchDropdownV2";


interface SearchBarV2Props {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const placeholders = [
  "Search for traditional Indian brass utensils...",
  "Search for pure copper water bottles...",
  "Search for traditional bronze cookware...",
  "Search for authentic Indian kitchenware...",
  "Search for heritage cookware and servers..."
];

export default function SearchBarV2({ isOpen, onOpenChange }: SearchBarV2Props) {
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState<SearchProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Running animated placeholder text
  const [placeholder, setPlaceholder] = useState("");
  const [currentPlaceholderIdx, setCurrentPlaceholderIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const fullText = placeholders[currentPlaceholderIdx];

    const handleTyping = () => {
      if (!isDeleting) {
        setPlaceholder(fullText.substring(0, placeholder.length + 1));
        setTypingSpeed(75);

        if (placeholder === fullText) {
          timer = setTimeout(() => setIsDeleting(true), 2500);
          return;
        }
      } else {
        setPlaceholder(fullText.substring(0, placeholder.length - 1));
        setTypingSpeed(35);

        if (placeholder === "") {
          setIsDeleting(false);
          setCurrentPlaceholderIdx((prev) => (prev + 1) % placeholders.length);
          setTypingSpeed(100);
          return;
        }
      }

      timer = setTimeout(handleTyping, typingSpeed);
    };

    timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [placeholder, isDeleting, currentPlaceholderIdx, typingSpeed]);
  const { fastPush } = useFastRouter();

  const isControlled = isOpen !== undefined;
  const isDropdownOpen = isControlled ? isOpen : showDropdown;

  const setDropdownOpen = (open: boolean) => {
    setShowDropdown(open);
    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  // Sync controlled prop changes
  useEffect(() => {
    if (isControlled) {
      setShowDropdown(isOpen);
    }
  }, [isOpen, isControlled]);

  // 1. Debounce and fetch suggestions based on 3-char minimum
  useEffect(() => {
    if (searchText.trim().length < 3) {
      setSuggestions([]);
      setTotalCount(0);
      setLoading(false);
      setDropdownOpen(false);
      return;
    }

    setLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await fetchSearchSuggestion(searchText);
        setSuggestions(res.data.products || []);
        setTotalCount(res.data.totalItems || 0);
        setDropdownOpen(true);
      } catch (err) {
        console.error("Search suggestions fetch failed:", err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchText]);

  // 2. Click outside listener to auto-close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isControlled, isDropdownOpen]);

  const handleSearchSubmit = () => {
    if (searchText.trim().length === 0) return;
    setDropdownOpen(false);
    fastPush(`/search-grid/${encodeURIComponent(searchText.trim())}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchSubmit();
    }
  };


  return (
    <div className="relative flex-1 max-w-[760px] z-40" ref={dropdownRef}>
      {/* Search Input Box Wrapper - Explicit 12px rounded corners */}
      <div className="flex items-center w-full h-[48px] border border-slate-200 focus-within:border-[var(--brand-accent)] rounded-[12px] overflow-hidden bg-white focus-within:ring-4 focus-within:ring-[var(--brand-accent)]/10 transition-all duration-200">
        <input
          type="text"
          placeholder={placeholder}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setDropdownOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchText.trim().length >= 3) {
              setDropdownOpen(true);
            }
          }}
          aria-label="Search items"
          className="flex-grow min-w-0 pl-6 pr-4 h-full text-sm outline-none text-slate-800 placeholder-slate-400 font-sans"
        />
        
        {/* Taller Search Button CTA - Rounded-r 12px corners */}
        <button
          onClick={handleSearchSubmit}
          aria-label="Search"
          className="h-full w-[48px] bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white flex items-center justify-center transition-colors duration-200 cursor-pointer rounded-r-[12px] shrink-0"
        >
          <Search className="w-[20px] h-[20px]" />
        </button>
      </div>

      {/* Expanded Suggestions & Previews Dropdown Component */}
      <SearchDropdownV2
        isOpen={Boolean(isDropdownOpen && searchText.trim().length >= 3)}
        loading={loading}
        suggestions={suggestions}
        totalCount={totalCount}
        onSelectProduct={() => {
          setDropdownOpen(false);
          setSearchText("");
        }}
        onViewAll={handleSearchSubmit}
      />
    </div>
  );
}

